
import { useDisplayPaneStore } from "@/stores/ssaStore/displayPane";
import { storeToRefs } from "pinia";
import { useSsaStore } from "@/stores/ssaStore";
import { useActiveStore } from "@/stores/ssaStore/active";
import { useLayerStore } from "@/stores/ssaStore/layer";
import { usePvwStore } from "@/stores/ssaStore/pvw";
import { usePickingStore } from "@/stores/ssaStore/picking";
import { useBusyStore } from "@/stores/ssaStore/busy";
import { useHierarchyStore } from "@/stores/hierarchyStore";
import { defineComponent } from "vue";

export default defineComponent({
	name: "imdex-slicing",
	props: {
		config: {
			type: Object,
			default: () => ({}) as any
		},
		visualized: {
			type: Boolean,
			default: false
		}
	},
	setup() {
		const pickStore = usePickingStore();
		const displayPaneStore = useDisplayPaneStore();
		const { SSA_ACTIVE_DATASET: activeDataset } = storeToRefs(useActiveStore());
		const { SSA_SELECTED_DATASETS: selectedDatasets } = storeToRefs(useActiveStore());
		const { hierarchyLayerSelections } = storeToRefs(useHierarchyStore());
		const { dataset: datasets } = storeToRefs(useActiveStore());
		const { displayStates } = storeToRefs(displayPaneStore);
		const { SSA_REPRESENTATION_STATES: representationStates, SSA_LAYER_STATES: layerStates } =
			storeToRefs(useLayerStore());
		const { HIERARCHY_STATE: hierarchyState } = storeToRefs(useHierarchyStore());
		const ssaStore = useSsaStore();
		const activeStore = useActiveStore();
		const { view: activeView } = storeToRefs(useActiveStore());
		const { BUSY_IS: isBusy } = storeToRefs(useBusyStore());
		const pvwStore = usePvwStore();
		const {
			PVW_FILTER_SLICE_ENABLE: enablePvwSlice,
			PVW_FILTER_SLICE_DISABLE: disablePvwSlice,
			PVW_FILTER_SLICE_ADD: addPvwSlice,
			PVW_FILTER_SLICE_REMOVE: removePvwSlice,
			PVW_FILTER_SLICE_UPDATE: updatePvwSlice
		} = usePvwStore();

		return {
			hierarchyState,
			datasets,
			activeView,
			enablePvwSlice,
			disablePvwSlice,
			addPvwSlice,
			removePvwSlice,
			hierarchyLayerSelections,
			updatePvwSlice,
			activeDataset,
			pickStore,
			displayStates,
			representationStates,
			layerStates,
			ssaStore,
			activeStore,
			pvwStore,
			isBusy,
			selectedDatasets
		};
	},
	data() {
		return {
			editSlice: -1 as any,
			outsideClickListener: null as any,
			tempSlices: null as any,
			tempActiveLayer: null as any,
			newestSliceAddedIndex: null as any
		};
	},
	mounted() {
		this.tempActiveLayer = this.activeLayer;
	},
	watch: {
		selectedDatasets: {
			deep: true,
			async handler(v) {
				console.log(v);
				if (v && v.length) {
					for (const layer in this.layerStates[this.activeStore.view]) {
						const datasetId = layer.split("/")[0];

						if (!v[datasetId]) {
							this.layerStates[this.activeStore.view][layer].slices.forEach(
								async (slice: any) => {
									if (slice.active) {
										slice.active = false;
										await this.updatePvwSlice({
											layerName: layer,
											viewId: this.activeStore.view,
											filter: slice
										});
									}
								}
							);
						}
					}
				}
			}
		},
		hierarchyLayerSelections: {
			deep: true,
			handler(v) {
				//	this.$nextTick(() => {
				if (this.hasDataLoaded) {
					//this.activeStore.SSA_ACTIVE_LAYER_SET(activeLayers[0].layerName);
				} else {
					if (this.tempSlices) {
						const activeSlice = this.tempSlices.filter((item: any) => {
							return item.active;
						});

						if (activeSlice.length) {
							activeSlice[0].active = false;
							this.updatePvwSlice({
								layerName: this.tempActiveLayer.split("__slice")[0],
								viewId: this.activeStore.view,
								filter: activeSlice[0]
							});
						}
					} else {
						return;
					}
				}
				//});
			}
		},
		activeLayer: {
			deep: true,
			handler(v) {
				if (v && v != "") {
					this.tempActiveLayer = v;
				}
			}
		},
		slices: {
			deep: true,
			handler(v) {
				if (v && v.length) {
					this.tempSlices = v;
				}
			}
		},
		newestSliceAddedIndex(v: number) {
			if (this.newestSliceAddedIndex !== null) {
				this.openSliceOptions(v);
			}
		},
		async visualized(v) {
			if (v) {
				this.enableSlice();
			} else {
				this.disableSlice();
			}
		}
	},
	methods: {
		calculateNormalVector(azimuth: number, dip: number) {
			// Helper function to convert dip/azi to x, y, z normal
			const azimuthRad = (Math.PI / 180) * Number(azimuth);
			const dipRad = (Math.PI / 180) * (90 + Number(dip));

			const x = Math.cos(dipRad) * Math.sin(azimuthRad);
			const y = Math.cos(dipRad) * Math.cos(azimuthRad);
			const z = Math.sin(dipRad);
			return [x, y, z];
		},
		calculateAzimuthDipFromNormal(x: number, y: number, z: number) {
			// Helper function to convert x, y, z normal back to azimuth and dip
			const azimuth = Math.atan2(x, y) * (180 / Math.PI);
			const hypotenuse = Math.sqrt(x * x + y * y);
			const dip = Math.atan2(z, hypotenuse) * (180 / Math.PI) - 90;

			// Adjusting azimuth to be between 0 and 360 degrees
			const adjustedAzimuth = azimuth < 0 ? 360 + azimuth : azimuth;

			return {
				azimuth: adjustedAzimuth,
				dip: dip
			};
		},
		async changeNormal(id: number, type: string, value: any) {
			const slice =
				this.slices[
					this.slices.findIndex((item: any) => {
						return item.id == id;
					})
				];
			const cc: any = {
				dip: slice.dip,
				azimuth: slice.azimuth
			};
			cc[type] = value.target.value;

			slice.normal = this.calculateNormalVector(cc.azimuth, cc.dip);
			slice.action = { normal: slice.normal };

			await this.updatePvwSlice({
				layerName: this.activeStore.SSA_ACTIVE_LAYER.split("__slice")[0],
				viewId: this.activeStore.view,
				filter: slice
			});
		},
		async activateSlice(id: number) {
			const slice =
				this.slices[
					this.slices.findIndex((item: any) => {
						return item.id == id;
					})
				];
			slice.active = !slice.active;
			await this.updatePvwSlice({
				layerName: this.activeStore.SSA_ACTIVE_LAYER.split("__slice")[0],
				viewId: this.activeStore.view,
				filter: slice
			});
		},
		getSliceAxis(filter: any): any {
			let v;
			switch (filter.normal.toString()) {
				case "1,0,0":
					v = 0;
					break;
				case "-1,0,0":
					v = 0;
					break;
				case "0,1,0":
					v = 1;
					break;
				case "0,-1,0":
					v = 1;
					break;
				case "0,0,-1":
					v = 2;
					break;
				case "0,0,1":
					v = 2;
					break;
				default:
					v = 3;
			}
			return v;
		},
		openSliceOptions(idx: number): any {
			this.editSlice > -1 ? (this.editSlice = -1) : (this.editSlice = idx);

			this.$nextTick(() => {
				if (this.editSlice > -1) {
					window.addEventListener(
						"mousedown",
						((this.outsideClickListener as any) = (e: any) => {
							const [x, y] = [e.pageX, e.pageY];
							const elements = document.elementsFromPoint(x, y);
							for (let i = 0; i < elements.length; i++) {
								if (elements[i].classList.contains("slice-options")) {
									return;
								}
							}
							this.editSlice = -1;
							this.newestSliceAddedIndex = null;
							window.removeEventListener(
								"mousedown",
								this.outsideClickListener as any
							);
						})
					);

					const container: any = window.document.getElementById("pane-container");
					let sliceItemOrAddRef = (this.$refs as any)[`slice-${idx}`]?.[0];
					const sliceOptionsRef = (this.$refs as any)["slice-options"];

					if (!sliceItemOrAddRef || sliceItemOrAddRef.length === 0) {
						sliceItemOrAddRef = (this.$refs as any).addSlice;
					}
					const { left, top, width } = sliceItemOrAddRef.getBoundingClientRect();

					const { height: elHeight } = sliceOptionsRef.getBoundingClientRect();

					if (left - 250 < 0) {
						sliceOptionsRef.style.left = left + width + "px";
					} else {
						sliceOptionsRef.style.left = left - 280 + "px";
					}
					if (top + elHeight > window.innerHeight) {
						sliceOptionsRef.style.top = window.innerHeight - elHeight - 15 + "px";
					} else {
						sliceOptionsRef.style.top = top + "px";
					}
					container.appendChild(sliceOptionsRef);
					this.newestSliceAddedIndex = null;
				}
			});
		},
		async removeSlice(id: number) {
			await this.removePvwSlice({
				layerName: this.tempActiveLayer,
				viewId: this.activeStore.view,
				filterId: id
			});
		},
		async updateSliceAxis(id: number, axis: number) {
			const slice =
				this.slices[
					this.slices.findIndex((item: any) => {
						return item.id == id;
					})
				];
			if (!slice) return;
			let normal = "x";
			switch (axis) {
				case 0:
					normal = "x";
					break;
				// case 0:
				// 	'-1,0,0'
				// 	break;
				case 1:
					normal = "y";
					break;
				// case 1:
				// 	'0,-1,0'
				// 	break;
				case 2:
					normal = "z";
					break;
				case 3:
					normal = "custom";
					break;
				// case '0,0,1':
				// 	v = 2;
				// 	break;
				default:
					normal = "custom";
			}
			slice.action = { normal: normal != "custom" ? normal : "custom" };
			if (normal == "custom") {
				slice.normal = [0.99, 0, 0];
			}

			await this.updatePvwSlice({
				layerName: this.activeStore.SSA_ACTIVE_LAYER.split("__slice")[0],
				viewId: this.activeStore.view,
				filter: slice
			});
		},
		async enableSlice() {
			if (this.canAddSlices) {
				return await this.enablePvwSlice({
					layerName: this.activeLayer,
					viewId: this.activeStore.view
				});
			}
			return false;
		},
		async disableSlice() {
			if (this.canAddSlices) {
				const layerNames = Object.keys(this.activeDataset.paraviewLayersInfoSplit);
				for (let i = 0; i < layerNames.length; i++) {
					await this.disablePvwSlice({
						layerName: layerNames[i],
						viewId: this.activeStore.view
					});
				}
			}
			return false;
		},
		async addSlice() {
			this.newestSliceAddedIndex = this.slices.length;

			if (
				this.displayMethods[
					this.displayMethods.findIndex((item: any) => item.name == "Blocks")
				].selected
			) {
				return await this.addPvwSlice({
					layerName: this.activeStore.SSA_ACTIVE_LAYER.split("__slice")[0],
					viewId: this.activeStore.view
				});
			}
		}
	},
	computed: {
		sliceNameMap(): any {
			return {
				0: "I",
				1: "J",
				2: "K",
				3: "CUSTOM"
			};
		},
		hasDataLoaded(): any {
			return (
				this.activeDataset &&
				this.hierarchyState[this.activeView] &&
				this.hierarchyState[this.activeView].selected.filter((item: any) =>
					item.includes(this.activeDataset.id)
				).length
			);
		},
		canAddSlices() {
			return this.displayMethods[
				this.displayMethods.findIndex((item: any) => item.name == "Blocks")
			]?.selected;
		},
		displayMethods(): any {
			return (
				(this.displayStates[this.activeView] &&
					this.datasets[this.activeView] &&
					this.displayStates[this.activeView][this.datasets[this.activeView].id]) ||
				[]
			);
		},
		activeLayer(): any {
			return (
				(this.activeStore.SSA_ACTIVE_LAYER &&
					this.activeStore.SSA_ACTIVE_LAYER.split("__slice")[0]) ||
				""
			);
		},
		slices(): any {
			const activeLayer =
				this.activeStore.SSA_ACTIVE_LAYER &&
				this.layerStates[this.activeStore.view] &&
				this.layerStates[this.activeStore.view][
					this.activeStore.SSA_ACTIVE_LAYER.split("__slice")[0]
				];
			return (
				activeLayer &&
				activeLayer.slices.map((item: any) => {
					return {
						...item,
						axis: this.getSliceAxis(item),
						dip:
							Math.round(
								this.calculateAzimuthDipFromNormal(
									item.normal[0],
									item.normal[1],
									item.normal[2]
								).dip * 100
							) / 100,
						azimuth:
							Math.round(
								this.calculateAzimuthDipFromNormal(
									item.normal[0],
									item.normal[1],
									item.normal[2]
								).azimuth * 100
							) / 100
					};
				})
			);
		},
		activeSlice: {
			get(): any {
				return this.slices[this.editSlice] || {};
			},
			set(v: any): any {
				console.log("v", v);
			}
		}
	}
});
